import { Component, DestroyRef, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import {
  FormBuilder,
  FormGroup,
  Validators,
  ReactiveFormsModule,
} from '@angular/forms';
import { Router } from '@angular/router';
import { forkJoin } from 'rxjs';
import {
  DsButtonV2Component,
  DsCardComponent,
  DsTagPillComponent,
} from '@customdesignsystem';
import {
  FUND_TAG_VARIANTS,
  IFundOption,
  MULTI_FUNDS,
  REGIMES,
  STEP_TWO,
  STEP_TWO_MULTI_FUNDS,
} from '../../constants/regime-fund.constant';
import { SUBSCRIPTION_URLS } from '../../constants/subscription-routes.constant';
import {
  ApvConfigService,
  SHOW_GENERATIONAL_FUNDS_DEFAULT,
} from '../../../core/services/firestore/apv-config.service';
import { ModalService } from '../../../core/services/modal/modal.service';
import { FundCatalogService } from '../../services/fund-catalog.service';
import { SubscriptionApiService } from '../../services/subscription-api.service';
import { SubscriptionStore } from '../../services/subscription.store';
import { GenerationalFundsModalComponent } from '../../components/generational-funds-modal/generational-funds-modal.component';
import { RegimeModalComponent } from '../../components/regime-modal/regime-modal.component';
import { MultiFundsModalComponent } from '../../components/multi-funds-modal/multi-funds-modal.component';
import { StepperLateralComponent } from '../../components/stepper-lateral/stepper-lateral.component';

const CURRENT_STEP_INDEX = 1;
const FUND_SKELETONS = [1, 2, 3, 4, 5];

@Component({
  selector: 'app-regime-fund-page',
  templateUrl: './regime-fund.page.html',
  styleUrl: './regime-fund.page.scss',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    StepperLateralComponent,
    DsButtonV2Component,
    DsCardComponent,
    DsTagPillComponent,
  ],
})
export class RegimeFundPage implements OnInit {
  stepTwo = STEP_TWO;
  regimes = REGIMES;
  readonly currentStepIndex = CURRENT_STEP_INDEX;
  readonly fundSkeletons = FUND_SKELETONS;
  readonly tagVariants = FUND_TAG_VARIANTS;

  isMultiFunds: boolean = !SHOW_GENERATIONAL_FUNDS_DEFAULT;
  generationalFunds: IFundOption[] = [];
  areFundsReady: boolean = false;
  rentabilityByFund: Record<string, string> = {};
  willBeRenew: boolean = false;
  isEditStepper: boolean = false;

  frmStepTwo: FormGroup;
  frmStepTwoPartTwo: FormGroup;

  constructor(
    private readonly formBuilder: FormBuilder,
    private readonly router: Router,
    private readonly api: SubscriptionApiService,
    private readonly store: SubscriptionStore,
    private readonly modalSvc: ModalService,
    private readonly apvConfigSvc: ApvConfigService,
    private readonly fundCatalog: FundCatalogService,
    private readonly destroyRef: DestroyRef
  ) {
    this.frmStepTwo = this.formBuilder.group({
      regime: ['', Validators.compose([Validators.required])],
    });

    this.frmStepTwoPartTwo = this.formBuilder.group({
      fund: ['', Validators.compose([Validators.required])],
    });
  }

  ngOnInit(): void {
    this.renewSubscription();
    this.loadSavedData();
    this.isEditStepper = this.store.editMode();
    this.loadFundsConfig();
  }

  private loadFundsConfig(): void {
    forkJoin([
      this.apvConfigSvc.showGenerationalFunds(),
      this.fundCatalog.generationalFunds(),
    ])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(([showGenerationalFunds, generationalFunds]) => {
        this.isMultiFunds =
          !showGenerationalFunds || generationalFunds.length === 0;
        this.generationalFunds = generationalFunds;
        this.areFundsReady = true;
        this.clearUnavailableFund();
        this.getAndSetRentability();
      });
  }

  private clearUnavailableFund(): void {
    const fund = this.selectedFund;

    if (
      fund &&
      !this.willBeRenew &&
      !this.funds.some((option) => option.id === fund)
    ) {
      this.frmStepTwoPartTwo.get('fund')?.setValue('');
    }
  }

  private loadSavedData(): void {
    const data = this.store.regimeFund();
    if (data) {
      this.frmStepTwo.patchValue({ regime: data.regime });
      this.frmStepTwoPartTwo.patchValue({ fund: data.fund });
    }
  }

  private async getAndSetRentability(): Promise<void> {
    if (!this.isMultiFunds) return;

    try {
      const rentability = await this.api.getRentability();

      this.rentabilityByFund = rentability.reduce(
        (acc, fund) => ({
          ...acc,
          [fund.id]: this.rentabilityText(fund.value),
        }),
        {} as Record<string, string>
      );
    } catch (error) {
      console.error('Error fetching rentability', error);
    }
  }

  private rentabilityText(value: number): string {
    const { label, period } = STEP_TWO_MULTI_FUNDS.rentability;

    return `${label} ${String(value).replace('.', ',')}% ${period}`;
  }

  get funds(): IFundOption[] {
    return this.isMultiFunds ? MULTI_FUNDS : this.generationalFunds;
  }

  get fundTexts() {
    return this.isMultiFunds
      ? {
          title: STEP_TWO_MULTI_FUNDS.fundTitle,
          subtitle: STEP_TWO_MULTI_FUNDS.fundSubtitle,
          link: STEP_TWO_MULTI_FUNDS.fundLink,
        }
      : {
          title: this.stepTwo.fundTitle,
          subtitle: this.stepTwo.fundSubtitle,
          link: this.stepTwo.fundLink,
        };
  }

  get selectedRegime(): string {
    return this.frmStepTwo.value.regime;
  }

  get selectedFund(): string {
    return this.frmStepTwoPartTwo.getRawValue().fund;
  }

  get showFunds(): boolean {
    return !!this.selectedRegime;
  }

  get canContinue(): boolean {
    return !!this.selectedRegime && !!this.selectedFund;
  }

  selectRegime(regimeId: string): void {
    const control = this.frmStepTwo.get('regime');

    control?.setValue(control.value === regimeId ? '' : regimeId);
  }

  selectFund(fundId: string): void {
    const control = this.frmStepTwoPartTwo.get('fund');

    if (control?.disabled) return;

    control?.setValue(control.value === fundId ? '' : fundId);
  }

  isFundLocked(fundId: string): boolean {
    return (
      this.frmStepTwoPartTwo.get('fund')?.disabled === true &&
      this.selectedFund !== fundId
    );
  }

  showMultifundsModal(): void {
    this.modalSvc.openCustomModal({
      component: this.isMultiFunds
        ? MultiFundsModalComponent
        : GenerationalFundsModalComponent,
      cssClass: 'apv-modal apv-modal--info',
    });
  }

  showRegimeModal(): void {
    this.modalSvc.openCustomModal({
      component: RegimeModalComponent,
      cssClass: 'apv-modal apv-modal--info',
    });
  }

  renewSubscription(): void {
    if (!this.hasActiveSubscriptionInSession()) return;

    const fundsDistribution = this.fundsDistributionInSession();
    if (fundsDistribution[0]) {
      this.willBeRenew = true;
      this.frmStepTwoPartTwo.setValue({ fund: fundsDistribution[0] });
      this.frmStepTwoPartTwo.get('fund')?.disable();
    }
  }

  private fundsDistributionInSession(): string[] {
    try {
      const stored = JSON.parse(
        sessionStorage.getItem('accountFundsDistribution') ?? '[]'
      );
      return Array.isArray(stored) ? stored : [];
    } catch {
      return [];
    }
  }

  private hasActiveSubscriptionInSession(): boolean {
    try {
      const stored = JSON.parse(
        sessionStorage.getItem('account_fund_CCV') ?? '[]'
      );
      const subscriptions = Array.isArray(stored) ? stored : [stored];
      return subscriptions.some((s) => s && s.movementType !== 'R');
    } catch {
      return false;
    }
  }

  goToNextStep(): void {
    if (!this.canContinue) return;

    this.saveData();
    this.goToStepThree();
  }

  private saveData(): void {
    this.store.setRegimeFund({
      regime: this.selectedRegime,
      fund: this.selectedFund,
    });
  }

  goBack(): void {
    if (this.isEditStepper) {
      this.backToConfirmation();
      return;
    }

    this.goToPreviousStep();
  }

  saveChanges(): void {
    if (!this.canContinue) return;

    this.saveData();
    this.backToConfirmation();
  }

  private backToConfirmation(): void {
    this.store.setEditMode(false);
    this.router.navigate([SUBSCRIPTION_URLS.confirm]);
  }

  goToPreviousStep(): void {
    this.router.navigate([SUBSCRIPTION_URLS.amount]);
  }

  goToStepThree(): void {
    this.router.navigate([
      this.store.hasEmployer()
        ? SUBSCRIPTION_URLS.employer
        : SUBSCRIPTION_URLS.addEmployer,
    ]);
  }
}
